import React, { useState, useCallback } from "react";
import {
    Typography, Box, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, TableSortLabel,
    Tooltip, Snackbar, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button as MuiButton
} from "@mui/material";
import { formatOADateTime } from "../utils/dateUtils";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "./atoms/Button";
import { BRAND_COLORS } from "../constants/taskStatuses";

/**
 * Parses notes from JSONB (array) or legacy TEXT (string) formats.
 * Returns an array of strings.
 */
const parseNotes = (notes) => {
    if (!notes) return [];
    if (Array.isArray(notes)) return notes;
    // Legacy plain-text fallback
    if (typeof notes === "string") {
        try {
            const parsed = JSON.parse(notes);
            return Array.isArray(parsed) ? parsed : [notes];
        } catch {
            return notes.trim() ? [notes] : [];
        }
    }
    return [];
};

/**
 * Format date helper for consistent display.
 */
const formatDate = (dateStr) => {
    return formatOADateTime(dateStr);
};


/**
 * Reusable line-item editor component.
 * Moved outside ObservationTable to prevent unmounting/remounting on every render
 * which was causing cursor jump issues.
 */
const LineItemEditor = ({ items, onChange, onAdd, onRemove, disabled }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#9CA3AF', minWidth: 20, textAlign: 'right' }}>
                    {index + 1}.
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={`Observation line ${index + 1}`}
                    value={item}
                    onChange={(e) => onChange(index, e.target.value)}
                    disabled={disabled}
                    // Only autoFocus on the last item if there's more than one (to handle newly added lines)
                    autoFocus={items.length > 1 && index === items.length - 1}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onAdd();
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            fontSize: '0.875rem'
                        }
                    }}
                />
                {items.length > 1 && (
                    <IconButton size="small" onClick={() => onRemove(index)} disabled={disabled} sx={{ color: '#9CA3AF' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>
        ))}
        <Box>
            <Button
                variant="secondary"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16, color: BRAND_COLORS.PRIMARY }} />}
                onClick={onAdd}
                disabled={disabled}
                sx={{ border: 'none', fontSize: '12px', color: BRAND_COLORS.PRIMARY, pl: 4 }}
            >
                Add Line
            </Button>
        </Box>
    </Box>
);

/**
 * Render notes as bullet list for display.
 */
const renderNotes = (notes) => {
    const items = parseNotes(notes);
    if (items.length === 0) return "—";

    return (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
            {items.map((line, i) => (
                <li key={i} style={{ color: '#4B5563', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {line}
                </li>
            ))}
        </ul>
    );
};

/**
 * Observation table for task notes.
 * Features: Add/Edit observations with dynamic line-item text fields, Copy button.
 * Created/Modified metadata is shown inline below the observation notes.
 * Older revision versions are collapsed by default.
 */
const ObservationTable = ({ observations = [], readOnly = false, onSave, onDelete, isSaving = false, users = [], currentUser = "", currentRole = "" }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newLineItems, setNewLineItems] = useState([""]);
    const [editingId, setEditingId] = useState(null);
    const [editLineItems, setEditLineItems] = useState([]);
    const [copySnackbar, setCopySnackbar] = useState(false);
    const [expandedRevisions, setExpandedRevisions] = useState({});
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const toggleRevisions = (obsId) => {
        setExpandedRevisions(prev => ({ ...prev, [obsId]: !prev[obsId] }));
    };

    const resolveUserDisplayName = (createdBy) => {
        if (!createdBy || createdBy.toLowerCase() === "system") return "System";
        const found = users.find(u =>
            u.displayName === createdBy ||
            u.email === createdBy ||
            `${u.firstName} ${u.lastName}`.trim() === createdBy
        );
        if (found) {
            return `${found.displayName} (${found.role?.roleName || "Internal User"})`;
        }
        return createdBy;
    };

    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Separate current observations from revisions
    const { currentObs, revisionMap } = React.useMemo(() => {
        const current = observations.filter(o => !o.parentId);
        const revisions = observations.filter(o => o.parentId);
        const map = {};
        revisions.forEach(r => {
            if (!map[r.parentId]) map[r.parentId] = [];
            map[r.parentId].push(r);
        });
        Object.values(map).forEach(arr => arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
        return { currentObs: current, revisionMap: map };
    }, [observations]);

    const sortedObservations = React.useMemo(() => {
        const items = [...currentObs];
        items.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'createdAt') {
                aVal = new Date(aVal || 0).getTime();
                bVal = new Date(bVal || 0).getTime();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return items;
    }, [currentObs, sortConfig]);

    /**
     * Role-based edit permission check.
     * Processor → own only | QA → own + Processor | Admin/AM → all
     */
    const canEditObs = (obs) => {
        if (obs.isLocked || obs.parentId) return false;
        const isOwner = obs.createdBy === currentUser;
        if (isOwner) return true;
        const isQA = currentRole === "QA";
        const isAdminOrAM = ["Admin", "Application Manager"].includes(currentRole);
        const creatorRole = (obs.createdBy || "").match(/\(([^)]+)\)$/)?.[1] || "";
        if (isQA && creatorRole === "Processor") return true;
        if (isAdminOrAM) return true;
        return false;
    };

    const getEditTooltip = (obs) => {
        if (obs.isLocked) return "Observation is locked";
        if (canEditObs(obs)) return "";
        return "You do not have permission to edit this observation";
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Track observation data changes to reset edit/add state
    const obsVersionKey = observations.map(o => `${o.id}-${o.updatedAt || JSON.stringify(o.notes)}`).join(',');
    React.useEffect(() => {
        setIsAdding(false);
        setEditingId(null);
    }, [obsVersionKey]);

    // --- Add Flow ---
    const handleAddClick = () => {
        setIsAdding(true);
        setNewLineItems([""]);
    };

    const handleAddLine = () => {
        setNewLineItems(prev => [...prev, ""]);
    };

    const handleNewLineChange = (index, value) => {
        setNewLineItems(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const handleRemoveNewLine = (index) => {
        setNewLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleNewSave = async () => {
        const items = newLineItems.filter(l => l.trim());
        if (items.length === 0 || isSaving) return;
        await onSave?.({ packageVersion: 1, notes: items });
        setIsAdding(false);
        setNewLineItems([""]);
    };

    // --- Edit Flow ---
    const handleEdit = (obs) => {
        setEditingId(obs.id);
        const items = parseNotes(obs.notes);
        setEditLineItems(items.length > 0 ? items : [""]);
    };

    const handleEditLineChange = (index, value) => {
        setEditLineItems(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const handleAddEditLine = () => {
        setEditLineItems(prev => [...prev, ""]);
    };

    const handleRemoveEditLine = (index) => {
        setEditLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async (obs) => {
        if (isSaving) return;
        const items = editLineItems.filter(l => l.trim());
        if (items.length === 0) return;

        // Optimization: If notes haven't changed (after trimming), just close edit mode
        const originalItems = parseNotes(obs.notes);
        if (JSON.stringify(items) === JSON.stringify(originalItems)) {
            setEditingId(null);
            return;
        }

        await onSave?.({ ...obs, notes: items, id: obs.id });
        setEditingId(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setIsAdding(false);
        setNewLineItems([""]);
        setEditLineItems([]);
    };

    /**
     * Copy observation text to clipboard in both plain-text and HTML formats.
     * HTML format ensures numbered lists paste correctly in Outlook.
     */
    const handleCopy = useCallback(async (notes) => {
        const items = parseNotes(notes);
        if (items.length === 0) return;
        try {
            // Build numbered list for plain text and HTML
            const plainText = items.map((l, i) => `${i + 1}. ${l}`).join('\n');
            const html = '<ol>' + items.map(l => `<li>${l}</li>`).join('') + '</ol>';

            const blob = new Blob([html], { type: 'text/html' });
            const textBlob = new Blob([plainText], { type: 'text/plain' });
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': blob,
                    'text/plain': textBlob
                })
            ]);
            setCopySnackbar(true);
        } catch {
            const plainText = items.map((l, i) => `${i + 1}. ${l}`).join('\n');
            await navigator.clipboard.writeText(plainText);
            setCopySnackbar(true);
        }
    }, []);


    return (
        <Box sx={{ position: 'relative' }} data-testid="oa-observation-table">
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                {!readOnly && (
                    <Button
                        variant="secondary"
                        startIcon={<AddIcon sx={{ color: BRAND_COLORS.PRIMARY }} />}
                        onClick={handleAddClick}
                        disabled={isAdding || isSaving}
                    >
                        Add Observation
                    </Button>
                )}
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                            <TableCell sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0' }}>
                                <TableSortLabel>Notes</TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0', width: 180 }}>
                                <TableSortLabel
                                    active={sortConfig.key === 'createdAt'}
                                    direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                                    onClick={() => handleSort('createdAt')}
                                >
                                    Created On
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 500, fontSize: "14px", color: "#64748B", borderBottom: '1px solid #E2E8F0', width: 120 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isAdding && (
                            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                                <TableCell>
                                    <LineItemEditor
                                        items={newLineItems}
                                        onChange={handleNewLineChange}
                                        onAdd={handleAddLine}
                                        onRemove={handleRemoveNewLine}
                                        disabled={isSaving}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" color="text.secondary">New Observation</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                        <Tooltip title="Save" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleNewSave}
                                                disabled={isSaving || !newLineItems.some(l => l.trim())}
                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                            >
                                                {isSaving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleCancel}
                                                disabled={isSaving}
                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                            >
                                                <CloseIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {sortedObservations.length === 0 && !isAdding ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No observations recorded yet.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedObservations.flatMap((obs) => {
                                const revisions = revisionMap[obs.id] || [];
                                const hasRevisions = revisions.length > 0;
                                const isRevisionsExpanded = !!expandedRevisions[obs.id];
                                const rows = [];

                                // Current observation row
                                rows.push(
                                    <TableRow key={obs.id} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }} data-testid={`oa-observation-row-${obs.id}`}>
                                        <TableCell sx={{ verticalAlign: 'top', py: 2, fontSize: "13px" }}>
                                            {editingId === obs.id ? (
                                                <LineItemEditor
                                                    items={editLineItems}
                                                    onChange={handleEditLineChange}
                                                    onAdd={handleAddEditLine}
                                                    onRemove={handleRemoveEditLine}
                                                    disabled={isSaving}
                                                />
                                            ) : (
                                                <>
                                                    {renderNotes(obs.notes)}
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9CA3AF', fontSize: '11px' }}>
                                                        {obs.modifiedBy && obs.modifiedBy !== obs.createdBy
                                                            ? <>Modified by {obs.modifiedBy}{obs.modifiedAt ? ` on ${formatDate(obs.modifiedAt)}` : ''}</>
                                                            : <>Created by {resolveUserDisplayName(obs.createdBy)}{obs.createdAt ? ` on ${formatDate(obs.createdAt)}` : ''}</>
                                                        }
                                                    </Typography>
                                                    {/* Revision toggle */}
                                                    {hasRevisions && (
                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer', color: BRAND_COLORS.PRIMARY }}
                                                            onClick={() => toggleRevisions(obs.id)}
                                                        >
                                                            {isRevisionsExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                                            <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 500 }}>
                                                                {isRevisionsExpanded ? 'Hide' : 'Show'} {revisions.length} previous version{revisions.length > 1 ? 's' : ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', py: 2, fontSize: "13px" }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "13px" }}>
                                                {obs.createdAt ? formatDate(obs.createdAt) : "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: 'top', py: 2 }}>
                                            <Box sx={{ display: "flex", gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                {editingId === obs.id ? (
                                                    <>
                                                        <Tooltip title="Save changes" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleSave(obs)}
                                                                disabled={isSaving || !editLineItems.some(l => l.trim())}
                                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                                            >
                                                                {isSaving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon sx={{ fontSize: 20 }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel editing" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleCancel}
                                                                disabled={isSaving}
                                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                                            >
                                                                <CloseIcon sx={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Tooltip title="Copy to clipboard" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCopy(obs.notes)}
                                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                                            >
                                                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {!readOnly && (
                                                            <Tooltip title={getEditTooltip(obs) || "Edit observation"} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={!canEditObs(obs) || isAdding || !!editingId || isSaving}
                                                                        onClick={() => handleEdit(obs)}
                                                                        sx={{ color: BRAND_COLORS.PRIMARY }}
                                                                    >
                                                                        <EditIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        {!readOnly && onDelete && (
                                                            <Tooltip title={canEditObs(obs) ? "Delete observation" : "You do not have permission to delete"} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={!canEditObs(obs) || isAdding || !!editingId || isSaving || isDeleting}
                                                                        onClick={() => setDeleteConfirmId(obs.id)}
                                                                        sx={{ color: '#EF4444' }}
                                                                    >
                                                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );

                                // Revision sub-rows (collapsed by default)
                                if (hasRevisions) {
                                    rows.push(
                                        <TableRow key={`rev-container-${obs.id}`} sx={{ backgroundColor: '#FAFAFA' }}>
                                            <TableCell colSpan={3} sx={{ p: 0, borderBottom: isRevisionsExpanded ? undefined : 'none' }}>
                                                <Collapse in={isRevisionsExpanded}>
                                                    <Box sx={{ pl: 4, py: 1 }}>
                                                        {revisions.map((rev, idx) => (
                                                            <Box key={rev.id} sx={{ py: 1.5, borderBottom: idx < revisions.length - 1 ? '1px dashed #E5E7EB' : 'none' }}>
                                                                <Typography variant="caption" sx={{ display: 'block', color: '#9CA3AF', fontWeight: 500, mb: 0.5, fontSize: '11px' }}>
                                                                    📝 Previous version
                                                                </Typography>
                                                                {renderNotes(rev.notes)}
                                                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9CA3AF', fontSize: '11px' }}>
                                                                    Created by {rev.createdBy || "—"}
                                                                    {rev.createdAt ? ` on ${formatDate(rev.createdAt)}` : ''}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                return rows;
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={copySnackbar}
                autoHideDuration={2000}
                onClose={() => setCopySnackbar(false)}
                message="Observation copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!deleteConfirmId}
                onClose={() => setDeleteConfirmId(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '16px' }}>Delete Observation</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '14px', color: '#4B5563' }}>
                        Are you sure you want to delete this observation? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
                    <Button
                        onClick={() => setDeleteConfirmId(null)}
                        variant="secondary"
                        height="36px"
                        sx={{ minWidth: 90 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!deleteConfirmId || !onDelete) return;
                            try {
                                setIsDeleting(true);
                                await onDelete(deleteConfirmId);
                                setDeleteConfirmId(null);
                            } catch {
                                setDeleteConfirmId(null);
                            } finally {
                                setIsDeleting(false);
                            }
                        }}
                        variant="primary"
                        disabled={isDeleting}
                        height="36px"
                        sx={{
                            minWidth: 90,
                            backgroundColor: '#EF4444',
                            '&:hover': { backgroundColor: '#DC2626' }
                        }}
                    >
                        {isDeleting ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ObservationTable;
